import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  Sparkles,
  Clock,
  Bookmark,
  MessageSquare,
  Tag,
  TrendingUp,
  Lock,
  Star,
  Zap,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  BarChart,
  FileText,
  ArrowRight,
  Target,
  Shield,
  Compass,
} from "lucide-react";

// Helper function for styling category tags
const getTagStyle = (tag) => {
  switch (tag?.toLowerCase()) {
    case "unmet need":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "feature request":
      return "bg-purple-100 text-purple-700 border-purple-200";
    case "competitor alert":
      return "bg-red-100 text-red-700 border-red-200";
    case "product idea":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "new trend":
      return "bg-teal-100 text-teal-700 border-teal-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

// Helper function for styling potential score
const getScoreStyle = (score) => {
  if (!score) return "text-gray-500";
  if (typeof score === "string" && score.includes("/")) {
    const numericScore = parseFloat(score.split("/")[0]);
    if (isNaN(numericScore)) return "text-gray-500";
    if (numericScore >= 8) return "text-green-600 font-semibold";
    if (numericScore >= 5) return "text-yellow-600 font-semibold";
    return "text-red-600 font-semibold";
  } else if (typeof score === "string") {
    const lowerScore = score.toLowerCase();
    if (lowerScore === "high") return "text-green-600 font-semibold";
    if (lowerScore === "medium") return "text-yellow-600 font-semibold";
    if (lowerScore === "low") return "text-red-600 font-semibold";
  }
  return "text-gray-500";
};

// Function to get numeric score value for comparing insights
const getNumericScore = (scoreString) => {
  if (!scoreString) return 0;

  if (typeof scoreString === "string" && scoreString.includes("/")) {
    const numericPart = parseFloat(scoreString.split("/")[0]);
    return isNaN(numericPart) ? 0 : numericPart;
  }

  if (typeof scoreString === "string") {
    const scoreMap = {
      high: 8,
      medium: 5,
      low: 3,
    };
    return scoreMap[scoreString.toLowerCase()] || 0;
  }

  return 0;
};

export function InsightCard({
  insight,
  index,
  bookmarkedPainPoints,
  handleToggleBookmark,
  isPremium = false,
  insightCredits = 0,
  isHighestRated = false,
  isBlurred = false,
  isExpanded = false,
  onToggleExpand,
}) {
  // --- Data Extraction ---
  const details = insight?.details || {};
  const queryKeywords = insight?.query_keywords || details?.query_keywords || [];

  // Get category details
  const categoryTag = details.category_tag || "";
  const categoryTagStyle = getTagStyle(categoryTag);
  const score = details.potential_score || "";
  const scoreStyle = getScoreStyle(score);

  // Theme colors based on category
  const getCategoryTheme = (category) => {
    switch (category?.toLowerCase()) {
      case "unmet need":
        return {
          primary: "from-blue-500 to-blue-600",
          light: "bg-blue-50",
          border: "border-blue-100",
          text: "text-blue-600",
          shadow: "shadow-blue-100/40",
          accent: "bg-blue-500",
        };
      case "feature request":
        return {
          primary: "from-purple-500 to-purple-600",
          light: "bg-purple-50",
          border: "border-purple-100",
          text: "text-purple-600",
          shadow: "shadow-purple-100/40",
          accent: "bg-purple-500",
        };
      case "competitor alert":
        return {
          primary: "from-red-500 to-red-600",
          light: "bg-red-50",
          border: "border-red-100",
          text: "text-red-600",
          shadow: "shadow-red-100/40",
          accent: "bg-red-500",
        };
      case "product idea":
        return {
          primary: "from-amber-500 to-amber-600",
          light: "bg-amber-50",
          border: "border-amber-100",
          text: "text-amber-600",
          shadow: "shadow-amber-100/40",
          accent: "bg-amber-500",
        };
      case "new trend":
        return {
          primary: "from-teal-500 to-teal-600",
          light: "bg-teal-50",
          border: "border-teal-100",
          text: "text-teal-600",
          shadow: "shadow-teal-100/40",
          accent: "bg-teal-500",
        };
      default:
        return {
          primary: "from-emerald-500 to-emerald-600",
          light: "bg-emerald-50",
          border: "border-emerald-100",
          text: "text-emerald-600",
          shadow: "shadow-emerald-100/40",
          accent: "bg-emerald-500",
        };
    }
  };

  const theme = getCategoryTheme(categoryTag);

  // If this card should be blurred (not highest-rated and no premium access)
  if (isBlurred) {
    return (
      <div className="relative">
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
          <Link
            to="/credits/"
            className={`bg-gradient-to-r ${theme.primary} hover:brightness-105 text-white px-5 py-3 rounded-xl text-sm font-medium transition-all duration-200 shadow-md flex items-center`}
          >
            <Lock className="h-4 w-4 mr-2" />
            Unlock this Insight
          </Link>
        </div>
        <div className="pointer-events-none opacity-50">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.3 }}
            className={`bg-white rounded-2xl overflow-hidden hover:shadow-xl border border-gray-100 ${theme.shadow}`}
          >
            {/* Status bar */}
            <div className={`h-1.5 bg-gradient-to-r ${theme.primary} w-full`}></div>

            <div className="p-6 sm:p-8">
              {/* Simplified header for blurred card */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center flex-wrap gap-x-3 gap-y-2">
                  <div className={`w-10 h-10 rounded-full ${theme.light} flex items-center justify-center flex-shrink-0`}>
                    <Sparkles size={20} className={theme.text} />
                  </div>
                  {categoryTag && (
                    <span className={`text-xs font-medium px-2.5 py-1.5 rounded-full flex items-center ${categoryTagStyle}`}>
                      <Tag size={12} className="mr-1.5 flex-shrink-0" /> {categoryTag}
                    </span>
                  )}
                  {score && (
                    <span className="text-xs bg-gray-50 px-2.5 py-1.5 rounded-full flex items-center">
                      <TrendingUp size={12} className="mr-1.5 flex-shrink-0" />
                      <span className={scoreStyle}>{score}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Main Content */}
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight mb-4">
                {details.validated_idea || "Insight Title Not Available"}
              </h3>

              {details.ai_summary && <p className="text-gray-600 text-base mb-6 leading-relaxed">{details.ai_summary}</p>}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // --- Render Normal Card ---
  const cardTheme = isHighestRated
    ? {
        border: "border-amber-200",
        shadow: "shadow-amber-100/50",
        highlight: "from-amber-400 to-amber-600",
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
        quoteBg: "bg-amber-50",
        quoteBorder: "border-amber-100",
        quoteColor: "text-amber-700",
      }
    : {
        border: `border-gray-100 hover:${theme.border}`,
        shadow: theme.shadow,
        highlight: theme.primary,
        iconBg: theme.light,
        iconColor: theme.text,
        quoteBg: theme.light,
        quoteBorder: theme.border,
        quoteColor: theme.text,
      };

  return (
    <motion.div
      key={insight.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl border ${cardTheme.border} ${cardTheme.shadow} relative group`}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full opacity-20 -mt-20 -mr-20 z-0"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-gray-50 to-gray-100 rounded-full opacity-20 -mb-16 -ml-16 z-0"></div>

      {/* Status bar */}
      <div className={`h-1.5 bg-gradient-to-r ${cardTheme.highlight} w-full transition-all duration-300`}></div>

      {/* Highest Rated Badge */}
      {isHighestRated && (
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-lg shadow-md flex items-center">
            <Star size={12} className="mr-1" /> Top Opportunity
          </div>
        </div>
      )}

      <div className="p-6 sm:p-8 relative z-1">
        {/* Header section */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-2">
            <div className={`w-11 h-11 rounded-lg ${cardTheme.iconBg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <Sparkles size={20} className={cardTheme.iconColor} />
            </div>

            <div className="flex flex-col">
              {/* Timestamp */}
              {insight.created_at && (
                <span className="text-xs text-gray-500 mb-1 flex items-center">
                  <Clock size={12} className="mr-1.5 flex-shrink-0" />
                  {formatDistanceToNow(new Date(insight.created_at), { addSuffix: true })}
                </span>
              )}

              {/* Category and Score */}
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                {categoryTag && (
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center border ${categoryTagStyle}`}>
                    <span>{categoryTag}</span>
                  </span>
                )}
                {score && (
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full flex items-center border ${
                      isHighestRated ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <TrendingUp size={12} className="mr-1.5 flex-shrink-0" />
                    <span className={scoreStyle}>{score}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Bookmark Button */}
          <button
            className={`p-2 rounded-full transition-all duration-200 flex-shrink-0 shadow-sm ${
              bookmarkedPainPoints.has(insight.id)
                ? "bg-emerald-100 text-emerald-600 hover:bg-emerald-200"
                : "bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            }`}
            onClick={handleToggleBookmark}
            title={bookmarkedPainPoints.has(insight.id) ? "Remove Bookmark" : "Save Bookmark"}
          >
            <Bookmark size={16} className={bookmarkedPainPoints.has(insight.id) ? "fill-current" : ""} />
          </button>
        </div>

        {/* Main Content */}
        <h3
          className={`text-xl sm:text-2xl font-bold ${
            isHighestRated ? "text-amber-800" : "text-gray-800"
          } leading-tight mb-4 group-hover:text-gray-900 transition-colors duration-300`}
        >
          {details.validated_idea || "Insight Title Not Available"}
        </h3>

        {details.ai_summary && <p className="text-gray-600 text-base mb-6 leading-relaxed">{details.ai_summary}</p>}

        {/* Voice of Customer */}
        {details.voice_of_customer && (
          <div className="mb-8 relative">
            <div
              className={`absolute -left-3 top-0 bottom-0 w-1 bg-gradient-to-b ${
                isHighestRated ? "from-amber-400 to-amber-600" : theme.primary
              } rounded-full`}
            ></div>
            <div
              className={`${cardTheme.quoteBg} p-5 rounded-lg border ${cardTheme.quoteBorder} shadow-sm ml-1 backdrop-blur-sm bg-opacity-60`}
            >
              <div className="flex mb-2 items-center">
                <MessageSquare size={16} className={cardTheme.iconColor} />
                <span className={`text-sm font-medium ml-2 ${cardTheme.quoteColor}`}>Voice of Customer</span>
              </div>
              <p className="text-gray-700 text-base sm:text-lg italic leading-relaxed">"{details.voice_of_customer}"</p>
            </div>
          </div>
        )}

        {/* Analysis Teaser */}
        <div className="mb-6 bg-gray-50 border border-gray-100 rounded-xl p-4">
          <div className="flex items-center mb-2">
            <ChevronDown size={16} className="text-gray-500 mr-2" />
            <h4 className="text-sm font-semibold text-gray-700">Analysis Includes:</h4>
          </div>
          <div className="space-y-2 pl-4">
            <div className="flex items-center text-xs text-gray-500">
              <div className="w-1 h-1 bg-gray-400 rounded-full mr-2"></div>
              <span>Problem Evidence & Validation</span>
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <div className="w-1 h-1 bg-gray-400 rounded-full mr-2"></div>
              <span>Solution Concept Development</span>
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <div className="w-1 h-1 bg-gray-400 rounded-full mr-2"></div>
              <span>Strategic Implementation Plan</span>
            </div>
          </div>
        </div>

        {/* Conditional Button - Either View Analysis or Unlock */}
        {isPremium || insightCredits > 0 ? (
          // For premium users or users with credits
          <button
            onClick={onToggleExpand} // Use the passed toggle function
            className={`w-full bg-gradient-to-r ${theme.primary} hover:brightness-105 text-white py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center font-medium text-sm shadow-md group`}
          >
            <span>{isExpanded ? "Hide Details" : "View Complete Analysis"}</span>
            {isExpanded ? (
              <ChevronUp size={16} className="ml-2 group-hover:scale-110 transition-transform duration-300" />
            ) : (
              <ChevronDown size={16} className="ml-2 group-hover:scale-110 transition-transform duration-300" />
            )}
          </button>
        ) : (
          // Unlock button for non-premium users
          <Link
            to="/credits/"
            className={`w-full bg-gradient-to-r ${theme.primary} hover:brightness-105 text-white py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center font-medium text-sm shadow-md group`}
          >
            <Lock size={18} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
            <span>Unlock Complete Analysis</span>
            <ChevronRight
              size={16}
              className="ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300"
            />
          </Link>
        )}

        {/* Expanded content */}
        <AnimatePresence>
          {(isPremium || insightCredits > 0) && isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-6 pt-6 border-t border-gray-100"
            >
              {/* Core Problem */}
              {details.core_problem && details.core_problem.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center mb-3">
                    <div className={`w-8 h-8 rounded-full ${theme.light} flex items-center justify-center mr-2`}>
                      <AlertTriangle size={16} className={theme.text} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Core Problem</h4>
                  </div>
                  <ul className="space-y-3 bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                    {details.core_problem.map((problem, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 mr-2.5 flex-shrink-0"></div>
                        <p className="text-gray-700">{problem}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Underserved Gap */}
              {details.underserved_gap && details.underserved_gap.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center mb-3">
                    <div className={`w-8 h-8 rounded-full ${theme.light} flex items-center justify-center mr-2`}>
                      <Target size={16} className={theme.text} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Underserved Gap</h4>
                  </div>
                  <ul className="space-y-3 bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                    {details.underserved_gap.map((gap, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 mr-2.5 flex-shrink-0"></div>
                        <p className="text-gray-700">{gap}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Problem Evidence */}
              {details.problem_evidence && details.problem_evidence.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center mb-3">
                    <div className={`w-8 h-8 rounded-full ${theme.light} flex items-center justify-center mr-2`}>
                      <CheckCircle size={16} className={theme.text} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Problem Evidence</h4>
                  </div>
                  <ul className="space-y-3 bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                    {details.problem_evidence.map((evidence, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 mr-2.5 flex-shrink-0"></div>
                        <p className="text-gray-700">{evidence}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Solution Concept */}
              {details.solution_concept && (
                <div className="mb-6">
                  <div className="flex items-center mb-3">
                    <div className={`w-8 h-8 rounded-full ${theme.light} flex items-center justify-center mr-2`}>
                      <Lightbulb size={16} className={theme.text} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Solution Concept</h4>
                  </div>
                  <div className={`${theme.light} rounded-lg p-4 border ${theme.border} shadow-sm`}>
                    <p className="text-gray-700">{details.solution_concept}</p>
                  </div>
                </div>
              )}

              {/* Strategic Angle */}
              {details.strategic_angle && details.strategic_angle.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center mb-3">
                    <div className={`w-8 h-8 rounded-full ${theme.light} flex items-center justify-center mr-2`}>
                      <Compass size={16} className={theme.text} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Strategic Angle</h4>
                  </div>
                  <ul className="space-y-3 bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                    {details.strategic_angle.map((strategy, idx) => (
                      <li key={idx} className="flex items-start text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 mr-2.5 flex-shrink-0"></div>
                        <p className="text-gray-700">{strategy}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Overall Assessment */}
              {details.ai_overall_assessment && (
                <div className="mb-6">
                  <div className="flex items-center mb-3">
                    <div className={`w-8 h-8 rounded-full ${theme.light} flex items-center justify-center mr-2`}>
                      <BarChart size={16} className={theme.text} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">AI Overall Assessment</h4>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-100 shadow-sm">
                    <p className="text-gray-700">{details.ai_overall_assessment}</p>
                  </div>
                </div>
              )}

              {/* Business Plan CTA - Only if highest rated */}

              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-blue-900">Ready to Launch This Business?</h4>
                      <p className="text-blue-700 mt-2 mb-4">
                        Generate your complete launch kit with market analysis, financial projections, and step-by-step execution plan.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                          Market Size & Competitor Analysis
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                          Financial Projections & Funding Strategy
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                          Complete Marketing & Launch Plan
                        </div>
                      </div>
                      <Link
                        to="/report"
                        onClick={() => {
                          localStorage.setItem(
                            "businessPlanInput",
                            `${details?.validated_idea || ""}\n\n` +
                              `Problem: ${details?.core_problem?.[0] || ""}\n` +
                              `Market Gap: ${details?.underserved_gap?.[0] || ""}\n` +
                              `Evidence: ${details?.problem_evidence?.[0] || ""}\n` +
                              `Solution: ${details?.solution_concept || ""}\n` +
                              `Strategy: ${details?.strategic_angle?.[0] || ""}\n` +
                              `Customer Voice: ${details?.voice_of_customer || ""}`
                          );
                        }}
                        className="mt-6 inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-md hover:shadow-lg group"
                      >
                        <FileText className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                        Generate Launch Kit Now
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// Helper function for finding highest rated insight in a group
export function findHighestRatedInsight(items) {
  if (!items || !items.length) return null;

  return items.reduce((highest, current) => {
    if (current.details?.type === "personas") return highest; // Skip persona cards

    // Get score values
    const highestScore = getNumericScore(highest?.details?.potential_score);
    const currentScore = getNumericScore(current?.details?.potential_score);

    // Return the higher scored item
    return currentScore > highestScore ? current : highest;
  }, items.filter((item) => item.details?.type !== "personas")[0] || null);
}
